import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Hls from "hls.js";
import {
  api,
  AudioTrack,
  Caption,
  MediaJob,
  StreamVideo,
  embedCode,
  embedUrl,
  wrapperEmbedCode,
  wrapperEmbedUrl,
} from "../lib/api";
import { CopyField } from "../components/CopyField";
import { LANGUAGES, languageName } from "../lib/languages";
import { SourceLangBanner } from "../components/SourceLangBanner";
import { GenerationPanel } from "../components/GenerationPanel";

export function VideoDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [video, setVideo] = useState<StreamVideo | null>(null);
  const [customerDomain, setCustomerDomain] = useState<string>("");
  const [captions, setCaptions] = useState<Caption[]>([]);
  const [audio, setAudio] = useState<AudioTrack[]>([]);
  const [jobs, setJobs] = useState<MediaJob[]>([]);
  const [error, setError] = useState<string | null>(null);

  async function loadAll() {
    if (!id) return;
    setError(null);
    try {
      const [v, me, c, a, j] = await Promise.all([
        api.getVideo(id),
        api.me(),
        api.listCaptions(id).catch(() => ({ captions: [] })),
        api.listAudio(id).catch(() => ({ audio: [] })),
        api.listJobs(id).catch(() => ({ jobs: [] })),
      ]);
      setVideo(v);
      setCustomerDomain(me.customerDomain ?? "");
      setCaptions(c.captions ?? []);
      setAudio(a.audio ?? []);
      setJobs(j.jobs ?? []);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!id) return null;
  if (error) return <div className="card p-4 border-red-300 bg-red-50 text-red-800 text-sm">{error}</div>;
  if (!video) return <div className="text-neutral-500 text-sm">Loading…</div>;

  const title = video.meta.name || video.uid;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <Link to="/" className="text-sm text-neutral-500 hover:text-neutral-900">← Library</Link>
        <button
          className="btn-danger"
          onClick={async () => {
            if (!confirm(`Delete "${title}"? This cannot be undone.`)) return;
            await api.deleteVideo(id);
            navigate("/");
          }}
        >
          Delete video
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="aspect-video bg-black rounded-lg overflow-hidden">
          {video.readyToStream && customerDomain ? (
            // poster: Stream's player fetches its poster once at load and
            // takes up to a minute to regenerate it after a thumbnail
            // change. Passing the cache-busted thumbnail explicitly (keyed
            // on `modified`, like the editor's preview) makes this admin
            // player show a new frame immediately after "Use this frame" —
            // the src change also forces the iframe to reload.
            <iframe
              src={`${embedUrl(customerDomain, id)}?poster=${encodeURIComponent(
                `${video.thumbnail}?v=${video.modified}`,
              )}`}
              className="w-full h-full"
              allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
              allowFullScreen
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-neutral-400 text-sm">
              {video.status.state} {video.status.pctComplete ? `· ${video.status.pctComplete}%` : ""}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <TitleEditor video={video} onSaved={loadAll} />
          <ThumbnailEditor video={video} onSaved={loadAll} />
        </div>
      </div>

      <SourceLangBanner video={video} onSaved={loadAll} />

      {customerDomain && (
        <EmbedSection
          videoId={id}
          title={title}
          customerDomain={customerDomain}
          audioTracks={audio}
        />
      )}

      <GenerationPanel video={video} jobs={jobs} onChanged={loadAll} />

      <CaptionsPanel videoId={id} captions={captions} onChanged={loadAll} />
      <AudioPanel videoId={id} tracks={audio} onChanged={loadAll} />
    </div>
  );
}

function EmbedSection({
  videoId,
  title,
  customerDomain,
  audioTracks,
}: {
  videoId: string;
  title: string;
  customerDomain: string;
  audioTracks: AudioTrack[];
}) {
  // When there are no dub tracks, just show the direct Cloudflare Stream
  // iframe URL — viewers get whatever Stream picks as the default audio.
  // When dubs exist, also list a wrapper URL per language. The wrapper
  // page is served by this Worker at /embed/:uid?audio=<label> and
  // auto-selects that track on load.
  const hasDubs = audioTracks.length > 0;

  return (
    <section className="card p-6 space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Embed URL & code</h2>
        {hasDubs && (
          <p className="text-sm text-neutral-500 mt-1">
            This video has multiple audio tracks. Use the language-specific URLs below to make a
            particular language the default when the player loads.
          </p>
        )}
      </div>

      {/* Default / original — direct Stream iframe URL */}
      <div className="space-y-2">
        <div className="text-xs uppercase tracking-wide text-neutral-500">
          {hasDubs ? "Original (default — Stream chooses)" : "Default"}
        </div>
        <CopyField label="Embed URL" value={embedUrl(customerDomain, videoId)} />
        <CopyField
          label="Embed code"
          value={embedCode(customerDomain, videoId, title)}
          multiline
        />
      </div>

      {/* Per-dub language wrappers */}
      {audioTracks.map((t) => (
        <div key={t.uid} className="space-y-2 border-t border-neutral-200 pt-4">
          <div className="text-xs uppercase tracking-wide text-neutral-500">
            Defaults to: {t.label}
          </div>
          <CopyField label="Embed URL" value={wrapperEmbedUrl(videoId, t.label)} />
          <CopyField
            label="Embed code"
            value={wrapperEmbedCode(videoId, title, t.label)}
            multiline
          />
          {t.status !== "ready" && (
            <p className="text-xs text-amber-700">
              This audio track is still <span className="font-mono">{t.status}</span> — the
              language switch will only work once it's <span className="font-mono">ready</span>.
            </p>
          )}
        </div>
      ))}
    </section>
  );
}

function TitleEditor({ video, onSaved }: { video: StreamVideo; onSaved: () => void }) {
  const [name, setName] = useState(video.meta.name || "");
  const [saving, setSaving] = useState(false);
  return (
    <div>
      <label className="label">Title</label>
      <div className="flex gap-2">
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        <button
          className="btn-primary"
          disabled={saving || name === (video.meta.name || "")}
          onClick={async () => {
            setSaving(true);
            try {
              await api.updateVideo(video.uid, { name });
              onSaved();
            } finally {
              setSaving(false);
            }
          }}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

function ThumbnailEditor({ video, onSaved }: { video: StreamVideo; onSaved: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const src = video.playback.hls;
    if (el.canPlayType("application/vnd.apple.mpegurl")) {
      // Safari has native HLS.
      el.src = src;
      return;
    }
    if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(src);
      hls.attachMedia(el);
      return () => hls.destroy();
    }
    // Last resort — most browsers won't play this directly, but try.
    el.src = src;
  }, [video.playback.hls]);

  // Cache-bust the thumbnail URL when the video gets modified (i.e. after
  // we apply a new frame), so the browser doesn't show the stale JPG.
  const currentThumbSrc = `${video.thumbnail}?v=${encodeURIComponent(video.modified)}`;

  return (
    <div>
      <label className="label">Thumbnail</label>

      <div className="flex items-start gap-4 mb-3">
        <div className="shrink-0">
          <div className="text-[11px] uppercase tracking-wide text-neutral-500 mb-1">
            Currently set
          </div>
          <img
            src={currentThumbSrc}
            alt="Current thumbnail"
            className="w-40 aspect-video object-cover rounded border border-neutral-200 bg-neutral-100"
          />
        </div>
        <p className="text-xs text-neutral-500 pt-5">
          To change it, play the video below, pause on the frame you want, then click
          <span className="font-medium"> "Use this frame"</span>. The library row and embed player
          will pick up the new thumbnail within a minute.
        </p>
      </div>

      <div className="rounded-lg overflow-hidden bg-black">
        <video
          ref={videoRef}
          controls
          preload="metadata"
          playsInline
          className="w-full aspect-video"
          onTimeUpdate={(e) => setCurrentTime((e.target as HTMLVideoElement).currentTime)}
          onSeeked={(e) => setCurrentTime((e.target as HTMLVideoElement).currentTime)}
        />
      </div>
      <div className="mt-3 flex items-center justify-between gap-3">
        <div className="text-xs text-neutral-600">
          Selected frame: <span className="font-mono">{formatTimestamp(currentTime)}</span>
        </div>
        <button
          className="btn-primary"
          disabled={saving || currentTime <= 0}
          onClick={async () => {
            setMsg(null);
            setSaving(true);
            try {
              await api.setThumbnailTimestamp(video.uid, currentTime);
              setMsg("Thumbnail updated.");
              onSaved();
            } catch (e) {
              setMsg((e as Error).message);
            } finally {
              setSaving(false);
            }
          }}
        >
          {saving ? "Saving…" : "Use this frame"}
        </button>
      </div>
      {msg && <p className="text-xs text-neutral-700 mt-2">{msg}</p>}
    </div>
  );
}

function formatTimestamp(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = (seconds % 60).toFixed(2);
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${s.padStart(5, "0")}`
    : `${m}:${s.padStart(5, "0")}`;
}

function CaptionsPanel({
  videoId,
  captions,
  onChanged,
}: {
  videoId: string;
  captions: Caption[];
  onChanged: () => void;
}) {
  const [lang, setLang] = useState("en");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function upload() {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      await api.uploadCaption(videoId, lang, file);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setUploading(false);
    }
  }

  return (
    <section className="card p-6">
      <h2 className="text-lg font-semibold mb-4">Captions</h2>
      {captions.length === 0 ? (
        <p className="text-sm text-neutral-500 mb-4">No captions yet.</p>
      ) : (
        <ul className="divide-y divide-neutral-200 mb-4">
          {captions.map((c) => (
            <li key={c.language} className="py-2 flex items-center justify-between">
              <div>
                <div className="font-medium text-sm">{languageName(c.language)}</div>
                <div className="text-xs text-neutral-500">{c.language} · {c.status}{c.generated ? " · auto" : ""}</div>
              </div>
              <button
                className="text-xs text-red-600 hover:underline"
                onClick={async () => {
                  if (!confirm(`Remove ${languageName(c.language)} captions?`)) return;
                  await api.deleteCaption(videoId, c.language);
                  onChanged();
                }}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-3 border-t border-neutral-200 pt-4">
        <div className="text-sm font-medium">Add caption file</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <select className="input" value={lang} onChange={(e) => setLang(e.target.value)}>
            {LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.name} — {l.code}
              </option>
            ))}
          </select>
          <input
            ref={fileRef}
            type="file"
            accept=".vtt,.srt,text/vtt,application/x-subrip"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="input file:mr-2 file:rounded file:border-0 file:bg-neutral-200 file:px-2 file:py-1 file:text-xs"
          />
          <button className="btn-primary" disabled={!file || uploading} onClick={upload}>
            {uploading ? "Uploading…" : "Upload"}
          </button>
        </div>
        <p className="text-xs text-neutral-500">VTT or SRT file. Replaces any existing caption for that language.</p>
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
    </section>
  );
}

function AudioPanel({
  videoId,
  tracks,
  onChanged,
}: {
  videoId: string;
  tracks: AudioTrack[];
  onChanged: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [label, setLabel] = useState("");
  const [language, setLanguage] = useState("");
  const [adding, setAdding] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function add() {
    if (!file || !label) return;
    setAdding(true);
    setProgress(0);
    setError(null);
    try {
      await api.addAudioFile(
        videoId,
        file,
        label,
        language || undefined,
        (pct) => setProgress(pct),
      );
      setFile(null);
      setLabel("");
      setLanguage("");
      setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setAdding(false);
    }
  }

  return (
    <section className="card p-6">
      <h2 className="text-lg font-semibold mb-1">Dub audio tracks</h2>
      <p className="text-sm text-neutral-500 mb-4">
        Add alternate audio (e.g. German dub, Spanish dub). Upload an .mp3, .m4a, .aac, or .wav file —
        it gets staged in R2 and pulled into Cloudflare Stream as an additional audio track.
      </p>

      {tracks.length === 0 ? (
        <p className="text-sm text-neutral-500 mb-4">No additional audio tracks yet.</p>
      ) : (
        <ul className="divide-y divide-neutral-200 mb-4">
          {tracks.map((t) => (
            <li key={t.uid} className="py-2 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">
                  {t.label}
                  {t.default && (
                    <span className="ml-2 inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-green-100 text-green-800 align-middle">
                      Default
                    </span>
                  )}
                </div>
                <div className="text-xs text-neutral-500">{t.status}</div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {!t.default && t.status === "ready" && (
                  <button
                    className="text-xs text-neutral-700 hover:text-neutral-900 hover:underline"
                    onClick={async () => {
                      await api.setAudioDefault(videoId, t.uid);
                      onChanged();
                    }}
                  >
                    Make default
                  </button>
                )}
                <button
                  className="text-xs text-red-600 hover:underline"
                  onClick={async () => {
                    if (!confirm(`Remove "${t.label}"?`)) return;
                    await api.deleteAudio(videoId, t.uid);
                    onChanged();
                  }}
                >
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-3 border-t border-neutral-200 pt-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <label className="label">Label</label>
            <input
              className="input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Deutsch"
              disabled={adding}
            />
          </div>
          <div>
            <label className="label">Language</label>
            <select
              className="input"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              disabled={adding}
            >
              <option value="">— optional —</option>
              {LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>{l.name} — {l.code}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="label">Audio file</label>
          <input
            ref={fileRef}
            type="file"
            accept="audio/*,.mp3,.m4a,.aac,.wav,.ogg,.opus"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            disabled={adding}
            className="input file:mr-2 file:rounded file:border-0 file:bg-neutral-200 file:px-2 file:py-1 file:text-xs"
          />
          {file && (
            <p className="text-xs text-neutral-500 mt-1">
              {file.name} · {(file.size / (1024 * 1024)).toFixed(1)} MB
            </p>
          )}
        </div>
        {adding && (
          <div>
            <div className="text-xs text-neutral-600 mb-1">
              {progress < 100 ? `Uploading… ${progress.toFixed(0)}%` : "Triggering Cloudflare Stream copy…"}
            </div>
            <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
              <div className="h-full bg-neutral-900 transition-[width] duration-150" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}
        <button className="btn-primary" disabled={!file || !label || adding} onClick={add}>
          {adding ? "Uploading…" : "Add audio track"}
        </button>
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
    </section>
  );
}
